(function (root) {
  'use strict';
  const idOf = value => String(value || '').split('/').pop();
  const text = value => String(value == null ? '' : value).trim().replace(/\s+/g, ' ');
  function labelParts(color, pair) {
    const originalColor = text(color), originalPair = text(pair);
    const total = originalPair.match(/(\d+)\s*pairs?\b/i);
    const all = originalColor.match(/\ball\s+(\d+)\s+colou?rs?\b/i);
    const each = /\b1\s*pair\s*each\b/i.test(originalColor);
    const count = all && each ? Number(all[1]) : total ? Number(total[1]) : null;
    let clean = originalColor.replace(/\s*\(\s*1\s*pair\s*each\s*\)\s*/ig, ' ');
    clean = clean.replace(/^\s*\d+\s*pairs?\s*[-:–—]\s*/i, '').replace(/\s*[-:–—]\s*\d+\s*pairs?\s*$/i, '').trim();
    const pack = count ? count + (count === 1 ? ' Pair' : ' Pairs') : originalPair;
    return { originalColor, originalPair, color: clean || originalColor, pack, count, label: (clean || originalColor) + (pack ? ' × ' + pack : '') };
  }
  function createModel(variants, config) {
    const color = Number(config.colorPosition) - 1;
    const pairPosition = Number(config.pairPosition);
    const pair = Number.isInteger(pairPosition) && pairPosition > 0 ? pairPosition - 1 : -1;
    if (!Number.isInteger(color) || color < 0 || color === pair) return null;
    if (variants.some(variant => !Array.isArray(variant.options) || color >= variant.options.length || pair >= variant.options.length)) return null;
    const records = variants.map((variant, index) => {
      const options = variant.options.map(text);
      const parts = pair >= 0 ? labelParts(options[color], options[pair]) : { originalColor:options[color], originalPair:'', color:options[color], pack:'', count:null, label:options[color] };
      return { variant, id: idOf(variant.id), options, index, ...parts, label: parts.label, otherKey: JSON.stringify(options.filter((_, i) => i !== color && i !== pair)), rawKey: JSON.stringify([options[color], options[pair]]) };
    });
    const collisions = new Map();
    records.forEach(record => { const key = JSON.stringify([record.label, record.otherKey]); const list = collisions.get(key) || []; list.push(record); collisions.set(key, list); });
    const fallbackKeys = new Set();
    collisions.forEach(list => {
      if (list.length < 2) return;
      const keep = list.find(record => record.originalColor === record.color) || list[0];
      list.filter(record => record !== keep).forEach(record => fallbackKeys.add(record.rawKey));
    });
    records.forEach(record => { if (fallbackKeys.has(record.rawKey)) record.label = record.originalColor; });
    const unique = new Map();
    records.forEach(record => {
      let key = JSON.stringify([record.label, record.otherKey]);
      if (unique.has(key)) {
        const prior = unique.get(key);
        prior.label = prior.originalColor + ' × ' + prior.originalPair;
        record.label = record.originalColor + ' × ' + record.originalPair;
      }
      unique.set(key, record);
    });
    const proof = new Set();
    records.forEach(record => {
      const key = JSON.stringify([record.label, record.otherKey]);
      if (proof.has(key)) throw new Error('Color / Pack label collision: ' + record.id);
      proof.add(key);
    });
    const groups = new Map();
    records.forEach(record => { const list = groups.get(record.label) || []; list.push(record); groups.set(record.label, list); });
    const independent = records[0]?.options.map((_, i) => i).filter(i => i !== color && i !== pair) || [];
    return { records, groups, color, pair, independent, byId: id => records.find(record => record.id === idOf(id)) };
  }
  root.FrilloraColorPackCore = Object.freeze({ createModel, labelParts, idOf });
  if (typeof document === 'undefined') return;
  let dropdownSequence = 0;
  const dropdownImage = value => {
    const source = typeof value === 'string' ? value : value?.src || value?.url || value?.preview_image?.src;
    if (!source) return '';
    try {
      const url = new URL(source, document.baseURI);
      if (!/^https?:$/.test(url.protocol)) return '';
      url.searchParams.set('width', '120');
      return url.href;
    } catch (_) { return ''; }
  };
  function createDropdown(options) {
    let settings = { placeholder:'Choose an option', soldOutLabel:'Sold out', ...options };
    let entries = [], value = String(settings.value ?? ''), activeValue = '', expanded = false, destroyed = false;
    let typeAhead = '', typeAheadAt = 0;
    const container = document.createElement('div'); container.className = 'fr-variant-dropdown';
    const trigger = document.createElement('button'); trigger.type = 'button'; trigger.className = 'fr-variant-dropdown__trigger'; trigger.dataset.frDropdownTrigger = '';
    trigger.setAttribute('role', 'combobox'); trigger.setAttribute('aria-haspopup', 'listbox'); trigger.setAttribute('aria-expanded', 'false'); trigger.setAttribute('aria-autocomplete', 'none');
    const menu = document.createElement('div'); menu.className = 'fr-variant-dropdown__menu'; menu.id = 'fr-variant-dropdown-' + (++dropdownSequence); menu.hidden = true; menu.setAttribute('role', 'listbox');
    trigger.setAttribute('aria-controls', menu.id); container.append(trigger, menu);
    const enabled = () => entries.filter(entry => !entry.disabled);
    const find = key => entries.find(entry => entry.value === key);
    function imageNode(source) {
      const url = dropdownImage(source) || dropdownImage(settings.fallbackImage);
      if (!url) return null;
      const image = document.createElement('img'); image.className = 'fr-variant-dropdown__image'; image.src = url; image.alt = ''; image.width = 44; image.height = 44; image.loading = 'lazy'; image.decoding = 'async';
      image.addEventListener('error', () => { const fallback = dropdownImage(settings.fallbackImage); if (fallback && image.src !== fallback) image.src = fallback; }, { once:true });
      return image;
    }
    function ensureActiveVisible() {
      const node = [...menu.children].find(item => item.dataset.frDropdownValue === activeValue);
      if (!node || !expanded) return;
      const itemBox = node.getBoundingClientRect(), menuBox = menu.getBoundingClientRect();
      if (itemBox.top < menuBox.top) menu.scrollTop -= menuBox.top - itemBox.top;
      else if (itemBox.bottom > menuBox.bottom) menu.scrollTop += itemBox.bottom - menuBox.bottom;
    }
    function setActive(key, scroll) {
      activeValue = key;
      let activeId = '';
      [...menu.children].forEach(node => {
        const active = node.dataset.frDropdownValue === key;
        node.classList.toggle('is-focused', active);
        if (active) activeId = node.id;
      });
      if (expanded && activeId) trigger.setAttribute('aria-activedescendant', activeId); else trigger.removeAttribute('aria-activedescendant');
      if (scroll) ensureActiveVisible();
    }
    function onOutside(event) { if (!container.contains(event.target)) close(); }
    function close() {
      expanded = false; menu.hidden = true; trigger.setAttribute('aria-expanded', 'false'); trigger.removeAttribute('aria-activedescendant');
      document.removeEventListener('pointerdown', onOutside, true);
    }
    function open(edge) {
      if (destroyed || !entries.length || settings.disabled) return;
      expanded = true; menu.hidden = false; trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('pointerdown', onOutside, true);
      const available = enabled();
      const target = edge === 'last' ? available.at(-1) : edge === 'first' ? available[0] : find(value) || available[0] || entries[0];
      setActive(target?.value || '', false);
      requestAnimationFrame(() => { if (!expanded || !container.isConnected) return; ensureActiveVisible(); menu.scrollIntoView({ block:'nearest', inline:'nearest' }); });
    }
    function paint() {
      const chosen = find(value), label = chosen?.label || settings.placeholder;
      const previousScroll = menu.scrollTop;
      trigger.replaceChildren();
      const image = chosen ? imageNode(chosen.image) : null;
      if (image && chosen) trigger.append(image);
      trigger.classList.toggle('has-image', Boolean(image && chosen));
      const selected = document.createElement('span'); selected.className = 'fr-variant-dropdown__value'; selected.textContent = label; trigger.append(selected);
      const caret = document.createElement('span'); caret.className = 'fr-variant-dropdown__caret'; caret.setAttribute('aria-hidden', 'true'); trigger.append(caret);
      trigger.disabled = Boolean(settings.disabled || !entries.length);
      trigger.setAttribute('aria-label', settings.label + ': ' + label);
      menu.setAttribute('aria-label', settings.label);
      const nodes = entries.map((entry, index) => {
        const node = document.createElement('div'); node.className = 'fr-variant-dropdown__option'; node.id = menu.id + '-option-' + index; node.dataset.frDropdownValue = entry.value; node.setAttribute('role', 'option');
        node.setAttribute('aria-selected', String(entry.value === value)); node.setAttribute('aria-disabled', String(entry.disabled));
        const thumb = imageNode(entry.image); if (thumb) node.append(thumb);
        node.classList.toggle('has-image', Boolean(thumb));
        const copy = document.createElement('span'); copy.className = 'fr-variant-dropdown__copy';
        const name = document.createElement('span'); name.className = 'fr-variant-dropdown__label'; name.textContent = entry.label; copy.append(name);
        if (entry.disabled) { const state = document.createElement('span'); state.className = 'fr-variant-dropdown__state'; state.textContent = settings.soldOutLabel; copy.append(state); }
        node.append(copy); return node;
      });
      menu.replaceChildren(...nodes); menu.scrollTop = previousScroll;
      if (expanded) setActive(find(activeValue)?.value || chosen?.value || enabled()[0]?.value || entries[0]?.value || '', false);
    }
    function update(next) {
      if (destroyed) return;
      settings = { ...settings, ...next };
      if (Object.prototype.hasOwnProperty.call(next, 'entries')) entries = next.entries.map(entry => ({ ...entry, value:String(entry.value), label:String(entry.label), disabled:Boolean(entry.disabled) }));
      if (Object.prototype.hasOwnProperty.call(next, 'value')) value = String(next.value ?? '');
      paint();
      if (settings.disabled || !entries.length) close();
    }
    function choose(key) {
      const entry = find(key);
      if (!entry || entry.disabled || settings.disabled) return;
      const previous = value; value = key; paint(); close();
      const accepted = settings.onChange?.(key);
      if (accepted === false && value === key) { value = previous; paint(); }
      if (container.isConnected) trigger.focus({ preventScroll:true });
    }
    trigger.addEventListener('click', () => { if (expanded) close(); else open(); });
    trigger.addEventListener('keydown', event => {
      const key = event.key;
      if (key === 'Escape') { if (expanded) { event.preventDefault(); event.stopPropagation(); close(); } return; }
      if (key === 'Tab') { close(); return; }
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End') {
        event.preventDefault();
        if (!expanded) { open(key === 'End' || key === 'ArrowUp' ? 'last' : key === 'Home' ? 'first' : undefined); return; }
        const available = enabled(); if (!available.length) return;
        const index = available.findIndex(entry => entry.value === activeValue);
        const next = key === 'Home' ? 0 : key === 'End' ? available.length - 1 : key === 'ArrowDown' ? (index + 1 + available.length) % available.length : index < 0 ? available.length - 1 : (index - 1 + available.length) % available.length;
        setActive(available[next].value, true); return;
      }
      if (key === 'Enter' || key === ' ') { event.preventDefault(); if (expanded) choose(activeValue); else open(); return; }
      if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault(); const now = Date.now(); typeAhead = now - typeAheadAt < 700 ? typeAhead + key : key; typeAheadAt = now;
        if (!expanded) open(); const match = enabled().find(entry => entry.label.toLocaleLowerCase().startsWith(typeAhead.toLocaleLowerCase())); if (match) setActive(match.value, true);
      }
    });
    menu.addEventListener('pointerdown', event => { if (event.target.closest('[data-fr-dropdown-value]')) event.preventDefault(); });
    menu.addEventListener('click', event => { const node = event.target.closest('[data-fr-dropdown-value]'); if (node && menu.contains(node)) choose(node.dataset.frDropdownValue); });
    container.addEventListener('focusout', event => { if (!container.contains(event.relatedTarget)) close(); });
    container.frColorDropdown = Object.freeze({ update, close, destroy() { close(); destroyed = true; } });
    update(settings);
    return container;
  }
  root.FrilloraColorDropdown = Object.freeze({ create:createDropdown });
  function createPdpColorSwatches(options) {
    let settings = { entries:[], value:'', ...options };
    let entries = [], value = String(settings.value ?? ''), destroyed = false;
    const container = document.createElement('div');
    container.className = 'fr-pdp-color-picker';
    container.setAttribute('data-fr-pdp-color-swatches', '');
    const selected = document.createElement('p');
    selected.className = 'fr-pdp-color-picker__selected';
    selected.setAttribute('aria-live', 'polite');
    const list = document.createElement('div');
    list.className = 'fr-pdp-color-picker__options';
    list.setAttribute('role', 'group');
    container.append(selected, list);
    const sourceDots = new Map();
    settings.scope.querySelectorAll('.fr-pdp-swatch-block [data-product-option-button]').forEach(button => {
      if (Number(button.dataset.optionPosition) - 1 !== settings.model.color) return;
      const dot = button.querySelector('.fr-color-dot');
      if (dot) sourceDots.set(text(button.dataset.optionValue), dot);
    });
    const buttonFor = key => [...list.children].find(button => button.dataset.frPdpSwatchValue === key);
    function restoreFocus(key) {
      const target = buttonFor(key);
      if (target && !target.disabled && document.activeElement !== target) target.focus({ preventScroll:true });
    }
    function paint() {
      const focused = document.activeElement;
      const focusedKey = list.contains(focused) ? focused?.closest?.('[data-fr-pdp-swatch-value]')?.dataset.frPdpSwatchValue : null;
      const chosen = entries.find(entry => entry.value === value);
      selected.textContent = chosen?.label || settings.placeholder || '';
      selected.hidden = !selected.textContent;
      list.setAttribute('aria-label', settings.label || 'Color');
      const buttons = entries.map(entry => {
        const record = settings.model.byId(entry.value);
        const originalDot = record && sourceDots.get(record.options[settings.model.color]);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fr-pdp-color-picker__choice';
        button.dataset.frPdpSwatchValue = entry.value;
        button.disabled = Boolean(settings.disabled || entry.disabled);
        button.classList.toggle('is-active', entry.value === value);
        button.setAttribute('aria-pressed', String(entry.value === value));
        button.setAttribute('aria-label', entry.label + (entry.disabled ? ': ' + (settings.soldOutLabel || 'Sold out') : ''));
        button.title = entry.label;
        if (originalDot) {
          const dot = originalDot.cloneNode(true);
          dot.setAttribute('aria-hidden', 'true');
          button.classList.add('has-dot');
          button.append(dot);
          if (settings.model.pair >= 0 && record.pack) {
            const pack = document.createElement('span');
            pack.className = 'fr-pdp-color-picker__pack';
            pack.textContent = record.pack;
            button.classList.add('has-pack');
            button.append(pack);
          }
        } else {
          const fallback = document.createElement('span');
          fallback.className = 'fr-pdp-color-picker__fallback';
          fallback.textContent = entry.label;
          button.append(fallback);
        }
        return button;
      });
      list.replaceChildren(...buttons);
      if (focusedKey !== null) restoreFocus(focusedKey);
    }
    function update(next) {
      if (destroyed) return;
      settings = { ...settings, ...next };
      if (Object.prototype.hasOwnProperty.call(next, 'entries')) entries = next.entries.map(entry => ({ ...entry, value:String(entry.value), label:String(entry.label), disabled:Boolean(entry.disabled) }));
      if (Object.prototype.hasOwnProperty.call(next, 'value')) value = String(next.value ?? '');
      paint();
    }
    function choose(key) {
      const entry = entries.find(item => item.value === key);
      if (destroyed || !entry || entry.disabled || settings.disabled) return;
      const previous = value;
      value = key;
      paint();
      const accepted = settings.onChange?.(key);
      if (accepted === false && value === key) { value = previous; paint(); }
      if (container.isConnected) restoreFocus(key);
    }
    list.addEventListener('click', event => {
      const button = event.target?.closest?.('[data-fr-pdp-swatch-value]');
      if (button && list.contains(button) && !button.disabled) choose(button.dataset.frPdpSwatchValue);
    });
    container.frColorDropdown = Object.freeze({ update, close() {}, destroy() { destroyed = true; } });
    update(settings);
    return container;
  }
  root.FrilloraPdpColorSwatches = Object.freeze({ create: createPdpColorSwatches });
  const instances = new WeakMap();
  function initialize() {
    document.querySelectorAll('[data-fr-color-pack-widget]').forEach(widget => {
      const scope = widget.closest('.fr-pdp-card');
      if (!scope || instances.has(scope)) return;
      const configNode = widget.querySelector('[data-fr-color-pack-config]'), variantsNode = scope.querySelector('[data-product-variants]');
      let config, variants;
      try { config = JSON.parse(configNode.textContent); variants = JSON.parse(variantsNode.textContent); } catch (_) { return; }
      const model = createModel(variants, config);
      if (!model || !model.records.length) return;
      const input = scope.querySelector('[data-product-variant-id]'), list = widget.querySelector('[data-fr-color-pack-list]'), notice = widget.querySelector('[data-fr-color-pack-notice]');
      if (!input || !list || !notice) return;
      const usePdpColorSwatches = document.body.classList.contains('fr-template-product') && !document.documentElement.classList.contains('fr-cart-pdp-embed') && !root.__frCartPdpEmbed;
      const createPicker = usePdpColorSwatches ? options => createPdpColorSwatches({ ...options, scope, model }) : createDropdown;
      const dropdown = createPicker({ label:config.label || (model.pair >= 0 ? 'Color / Pack' : 'Color'), entries:[], value:input.value, fallbackImage:config.fallbackImage, soldOutLabel:config.soldOut, onChange(value) {
        const accepted = select(model.byId(value), 'color-pack', false);
        if (accepted) notice.textContent = ''; else render();
        return accepted;
      } });
      list.replaceChildren(dropdown);
      const buttons = [...scope.querySelectorAll('[data-product-option-button]')];
      const nativeColor = scope.querySelector('.fr-pdp-swatch-block');
      const nativePair = buttons.find(button => Number(button.dataset.optionPosition) === model.pair + 1)?.closest('.fr-nx-selector');
      scope.dataset.frColorPack = 'true';
      nativeColor?.classList.add('fr-ncp-native');
      if (nativePair) { nativePair.classList.add('fr-ncp-native'); nativePair.parentElement.classList.remove('fr-nx-selectors--split'); }
      else buttons.filter(button => Number(button.dataset.optionPosition) === model.pair + 1).forEach(button => { button.closest('.fr-pair-card-grid')?.classList.add('fr-ncp-native'); const label = button.closest('.fr-pair-card-grid')?.previousElementSibling; if (label?.classList.contains('fr-product-form__label')) label.classList.add('fr-ncp-native'); });
      widget.hidden = false;
      const current = () => model.byId(input.value);
      const sameIndependent = (record, chosen) => model.independent.every(i => record.options[i] === chosen.options[i]);
      function select(record, reason, allowUnavailable) {
        if (!record || (!record.variant.available && !allowUnavailable)) return false;
        if (reason !== 'deep-link' && ((root.FrilloraCartEnhance?.isVariantSelectionBlocked?.() ?? root.FrilloraCartEnhance?.isPurchaseBlocked?.()) || root.FrilloraSilentPdpCartBlocked?.(scope.closest('.fr-pdp')))) return false;
        const detail = { variantId: record.id, allowUnavailable: Boolean(allowUnavailable), updateUrl: reason !== 'deep-link', reason, applied: false };
        scope.dispatchEvent(new CustomEvent('fr:select-variant', { detail }));
        if (!detail.applied || idOf(input.value) !== record.id) { notice.textContent = config.error; return false; }
        return true;
      }
      function render() {
        const chosen = current();
        if (!chosen) return;
        const nodes = [];
        model.groups.forEach((records, label) => {
          const matches = records.filter(record => sameIndependent(record, chosen));
          if (!matches.length) return;
          const record = matches.find(item => item.id === chosen.id) || matches.find(item => item.variant.available) || matches[0];
          const image = record.variant.featured_image || record.variant.featured_media?.preview_image || config.fallbackImage;
          nodes.push({ value:record.id, label, image, disabled:!record.variant.available });
        });
        dropdown.frColorDropdown.update({ entries:nodes, value:chosen.id });
        buttons.filter(button => model.independent.includes(Number(button.dataset.optionPosition) - 1)).forEach(button => {
          const position = Number(button.dataset.optionPosition) - 1;
          const available = model.records.some(record => record.variant.available && record.options[position] === button.dataset.optionValue && model.independent.every(i => i === position || record.options[i] === chosen.options[i]));
          button.disabled = !available; button.classList.toggle('is-disabled', !available);
          const sold = button.querySelector('[data-option-sold-out]'); if (sold) sold.hidden = available;
        });
      }
      const instance = { handleNativeOption(button, event) {
        const position = Number(button.dataset.optionPosition) - 1;
        if (!model.independent.includes(position)) return false;
        event.preventDefault(); event.stopImmediatePropagation();
        const chosen = current(), value = button.dataset.optionValue;
        if (!chosen) return true;
        const pool = model.records.filter(record => record.options[position] === value && model.independent.every(i => i === position || record.options[i] === chosen.options[i]));
        const exact = pool.find(record => record.label === chosen.label);
        const sale = pool.filter(record => record.variant.available);
        const next = exact || sale.find(record => record.options[model.color] === chosen.options[model.color]) || sale.find(record => record.pack === chosen.pack) || sale[0];
        if (!next) { notice.textContent = config.noOptions; return true; }
        if (select(next, 'independent-option', true)) notice.textContent = exact ? '' : config.updated.replace('{label}', next.label).replace('{size}', button.dataset.optionLabel || value);
        return true;
      } };
      instances.set(scope, instance);
      scope.addEventListener('fr:variant-selected', () => { render(); root.FrilloraSyncPdpPurchaseState?.(); });
      const deep = root.__frColorPackInitialVariant || new URL(document.baseURI).searchParams.get('variant');
      if (deep && model.byId(deep)) select(model.byId(deep), 'deep-link', true);
      render();
    });
  }
  root.FrilloraColorPack = Object.freeze({ handleNativeOption(scope, button, event) { return instances.get(scope)?.handleNativeOption(button, event) || false; } });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize); else initialize();
  document.addEventListener('shopify:section:load', initialize);
})(globalThis);
